import * as React from "react";
import { useEffect, useMemo, useState } from "react";
import Header from "../../Components/Header";
import { sendGetRequest, sendPostRequest } from "../../Client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "flowbite-react";
import { Notyf } from "notyf";
import "notyf/notyf.min.css";

const ProvidersTable = ({
  items,
  valuesById,
  handleChange,
  doUpdate,
  isUpdatingAny,
}) => (
  <div className="mb-10">
    <div className="flex items-center justify-between mb-3">
      <h4 className="text-sm font-semibold text-gray-900">Providers</h4>
      <div className="text-xs text-gray-500">{items.length} providers</div>
    </div>

    <div className="overflow-x-auto bg-white border border-gray-200 rounded-lg">
      <table className="w-full text-sm text-left text-gray-500 table-fixed">
        <colgroup>
          <col className="w-1/2" />
          <col className="w-1/2" />
          <col className="w-36" />
        </colgroup>

        <thead className="text-xs text-gray-700 uppercase bg-gray-50">
          <tr>
            <th scope="col" className="px-4 py-3">
              Provider
            </th>
            <th scope="col" className="px-4 py-3"></th>
          </tr>
        </thead>

        <tbody>
          {items.length === 0 ? (
            <tr>
              <td className="px-4 py-4 text-sm text-gray-500" colSpan={3}>
                No providers found.
              </td>
            </tr>
          ) : (
            items.map((p) => {
              const currentName = valuesById[p.providerID] ?? "";

              return (
                <tr key={p.providerID} className="border-b hover:bg-gray-50">


                  <td className="px-4 py-2">
                    <input
                      type="text"
                      value={currentName}
                      onChange={(e) =>
                        handleChange(p.providerID, e.target.value)
                      }
                      className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                      placeholder="Enter provider name…"
                    />
                  </td>

                  <td className="w-36 px-4 py-2 font-medium text-gray-900 whitespace-nowrap">
                    <Button
                      size="sm"
                      disabled={isUpdatingAny}
                      onClick={() =>
                        doUpdate({
                          providerID: p.providerID,
                          name: currentName,
                        })
                      }
                    >
                      Update
                    </Button>
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  </div>
);

export default function ProvidersManage() {
  const queryClient = useQueryClient();

  const notyf = useMemo(
    () =>
      new Notyf({
        position: { x: "right", y: "top" },
      }),
    []
  );

  // Load providers
  const { data, isLoading, isError } = useQuery({
    queryKey: ["mainproviders"],
    queryFn: () => sendGetRequest("mainproviders", {}),
  });

  // Adjust if your response shape differs
  const providers = data?.providers ?? [];

  // Local editable state for inputs, keyed by providerID
  const [valuesById, setValuesById] = useState({});

  // Seed inputs from server once per providerID
  useEffect(() => {
    if (!providers.length) return;

    setValuesById((prev) => {
      const next = { ...prev };

      for (const p of providers) {
        const serverVal = p.providerName ?? "";
        if (next[p.providerID] === undefined) {
          next[p.providerID] = serverVal;
        }
      }

      return next;
    });
  }, [providers]);

  const handleChange = (providerID, nextValue) => {
    setValuesById((prev) => ({ ...prev, [providerID]: nextValue }));
  };

  const updateProvider = async ({ providerID, name }) => {
    return sendPostRequest("mainprovider/update", { providerID, name });
  };

  const { mutateAsync: doUpdate, isPending: isUpdatingAny } = useMutation({
    mutationFn: updateProvider,

    // Optimistic update
    onMutate: async ({ providerID, name }) => {
      await queryClient.cancelQueries({ queryKey: ["mainproviders"] });

      const previous = queryClient.getQueryData(["mainproviders"]);

      queryClient.setQueryData(["mainproviders"], (old) => {
        if (!old?.providers) return old;
        return {
          ...old,
          providers: old.providers.map((p) =>
            p.providerID === providerID ? { ...p, name } : p
          ),
        };
      });

      return { previous };
    },

    onError: (err, _vars, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(["mainproviders"], ctx.previous);
      notyf.error("Could not update provider");
      console.log(err);
    },

    onSuccess: () => {
      notyf.success("Provider updated");
    },

    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["mainproviders"] });
    },
  });

  if (isLoading) {
    return (
      <>
        <Header />
        <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
          <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
            <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg p-6">
              <div className="text-sm text-gray-500">Loading providers…</div>
            </div>
          </div>
        </section>
      </>
    );
  }

  if (isError) {
    return (
      <>
        <Header />
        <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
          <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
            <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg p-6">
              <div className="text-sm text-red-600">
                Failed to load providers.
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }

  return (
    <>
      <Header />

      <section className="bg-gray-100 dark:bg-gray-900 py-3 sm:py-5">
        <div className="px-4 mx-auto max-w-screen-2xl lg:px-12 mb-80">
          <div className="relative overflow-hidden bg-white shadow-md dark:bg-gray-800 sm:rounded-lg">
            <div className="border-b border-gray-200 pb-5 sm:flex sm:items-center sm:justify-between w-[94%] ml-[3%] mt-[3%] pb-6">
              <h3 className="text-base font-semibold text-gray-900">
                Manage Providers
              </h3>
            </div>

            <div className="p-6">
              <ProvidersTable
                items={providers}
                valuesById={valuesById}
                handleChange={handleChange}
                doUpdate={doUpdate}
                isUpdatingAny={isUpdatingAny}
              />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
